import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Gauge,
  Filter,
  AlertTriangle,
  CheckCircle2,
  EyeOff,
  FileDown,
  PlusCircle,
  Search,
} from "lucide-react";

import { apiDownload, formatCLP, formatDate } from "../../lib/api";
import { cantidad } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import { AccionesLectura } from "./AccionesLectura";
import { IngresarLecturaModal } from "./IngresarLecturaModal";
import {
  listarLecturas,
  type EstadoLectura,
  type LecturaRead,
} from "./api";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { listarSectores } from "../sectores/api";
import { MESES } from "../cierres/estado";

/** Normaliza para búsqueda: minúsculas sin acentos. */
function normalizar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

const HOY = new Date();

const ESTADOS: EstadoLectura[] = [
  "Pendiente",
  "Emitida",
  "Pagada",
  "Vencida",
  "Anulada",
];

const ESTADO_TONE: Record<EstadoLectura, BadgeTone> = {
  Pendiente: "slate",
  Emitida: "brand",
  Pagada: "emerald",
  Vencida: "rose",
  Anulada: "slate",
};

const TIPO_TONE: Record<string, BadgeTone> = {
  Real: "emerald",
  Estimada: "amber",
  Ajuste: "slate",
};

function parseIntParam(s: string | null, fallback: number): number {
  if (!s) return fallback;
  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

export function LecturasListPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  // `state.desde` (p. ej. desde el detalle de un cierre) alimenta el
  // "Volver a …" del PageHeader: se conserva al cambiar mes/año.
  const location = useLocation();
  const mes = parseIntParam(searchParams.get("mes"), HOY.getMonth() + 1);
  const anio = parseIntParam(searchParams.get("anio"), HOY.getFullYear());

  const setMes = (v: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("mes", String(v));
    setSearchParams(next, { replace: true, state: location.state });
  };
  const setAnio = (v: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("anio", String(v));
    setSearchParams(next, { replace: true, state: location.state });
  };
  const [sectorFiltro, setSectorFiltro] = useState<string>("");
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoLectura | "TODOS">(
    (searchParams.get("estado") as EstadoLectura | null) ?? "TODOS",
  );
  // Seed de la búsqueda desde `?q=` — usado por el deep-link de la
  // Revisión de consumos para aterrizar en el socio del hallazgo.
  const [busqueda, setBusqueda] = useState(searchParams.get("q") ?? "");
  const qParam = searchParams.get("q");
  useEffect(() => {
    // Un deep-link nuevo (otro arranque) con la página ya montada.
    if (qParam !== null) setBusqueda(qParam);
  }, [qParam]);
  const [incluirAnuladas, setIncluirAnuladas] = useState(false);
  const [ingresando, setIngresando] = useState(false);

  const sectoresQ = useQuery({
    queryKey: ["sectores"],
    queryFn: listarSectores,
  });

  const lecturasQ = useQuery({
    queryKey: [
      "lecturas",
      mes,
      anio,
      sectorFiltro || null,
      incluirAnuladas,
    ],
    queryFn: () =>
      listarLecturas({
        mes,
        anio,
        sector_id: sectorFiltro ? Number(sectorFiltro) : null,
        incluir_anuladas: incluirAnuladas,
      }),
  });

  // Cargo contratos para enriquecer cada lectura con socio/arranque
  // (el endpoint sólo devuelve contrato_id).
  const contratosQ = useQuery({
    queryKey: ["contratos"],
    queryFn: fetchContratos,
  });

  const contratoById = useMemo(() => {
    const m = new Map<number, ContratoOperativo>();
    for (const c of contratosQ.data ?? []) m.set(c.id, c);
    return m;
  }, [contratosQ.data]);

  const lecturasFiltradas = useMemo(() => {
    let data = lecturasQ.data ?? [];
    if (estadoFiltro !== "TODOS") {
      data = data.filter((l) => l.estado === estadoFiltro);
    }
    const q = normalizar(busqueda.trim());
    if (q) {
      const terms = q.split(/\s+/);
      data = data.filter((l) => {
        const c = contratoById.get(l.contrato_id);
        const hay = normalizar(
          `${c?.socio_nombre ?? ""} ${c?.socio_rut ?? ""} ` +
            `${c?.arranque_codigo ?? ""} ${c?.arranque_direccion ?? ""} ` +
            `${l.numero_boleta ?? ""}`,
        );
        return terms.every((t) => hay.includes(t));
      });
    }
    return data;
  }, [lecturasQ.data, estadoFiltro, busqueda, contratoById]);

  const contratosConLectura = useMemo(() => {
    const s = new Set<number>();
    for (const l of lecturasQ.data ?? []) {
      if (l.estado !== "Anulada") s.add(l.contrato_id);
    }
    return s;
  }, [lecturasQ.data]);

  const stats = useMemo(() => {
    const data = lecturasQ.data ?? [];
    return {
      total: data.length,
      reales: data.filter((l) => l.tipo_lectura === "Real").length,
      estimadas: data.filter((l) => l.tipo_lectura === "Estimada").length,
      con_revision: data.filter((l) => l.flag_revision).length,
      total_m3: data.reduce((acc, l) => acc + l.consumo_m3, 0),
      total_facturado: data.reduce((acc, l) => acc + l.monto_total, 0),
    };
  }, [lecturasQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lecturas"
        icon={Gauge}
        subtitle={`Lecturas del periodo ${MESES[mes - 1]} ${anio}`}
        actions={
          <Button icon={PlusCircle} onClick={() => setIngresando(true)}>
            Ingresar lectura
          </Button>
        }
      />

      {ingresando && (
        <IngresarLecturaModal
          mes={mes}
          anio={anio}
          contratosConLectura={contratosConLectura}
          onClose={() => setIngresando(false)}
        />
      )}

      {/* Filtros */}
      <Card>
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-neutral-700">
          <Filter className="h-4 w-4" strokeWidth={1.5} />
          Filtros
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-neutral-700">Mes</label>
            <select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              className="mt-1 rounded-md border border-neutral-450 px-3 py-2 text-sm shadow-sm"
            >
              {MESES.map((nombre, idx) => (
                <option key={idx} value={idx + 1}>
                  {nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700">Año</label>
            <input
              type="number"
              min={2020}
              max={2100}
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              className="mt-1 w-28 rounded-md border border-neutral-450 px-3 py-2 text-sm shadow-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700">
              Sector
            </label>
            <select
              value={sectorFiltro}
              onChange={(e) => setSectorFiltro(e.target.value)}
              disabled={sectoresQ.isLoading}
              className="mt-1 rounded-md border border-neutral-450 px-3 py-2 text-sm shadow-sm"
            >
              <option value="">Todos los sectores</option>
              {[...(sectoresQ.data ?? [])]
                .sort((a, b) => a.nombre.localeCompare(b.nombre))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                    {s.codigo ? ` (${s.codigo})` : ""}
                  </option>
                ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700">Estado</label>
            <select
              value={estadoFiltro}
              onChange={(e) =>
                setEstadoFiltro(e.target.value as EstadoLectura | "TODOS")
              }
              className="mt-1 rounded-md border border-neutral-450 px-3 py-2 text-sm shadow-sm"
            >
              <option value="TODOS">Todos</option>
              {ESTADOS.map((e) => (
                <option key={e} value={e}>
                  {e}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-neutral-700">
              Buscar
            </label>
            <div className="relative mt-1">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500"
                strokeWidth={1.5}
              />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Socio, RUT, arranque, dirección o N° boleta"
                className="w-full rounded-md border border-neutral-450 py-2 pl-8 pr-3 text-sm shadow-sm"
              />
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
            <input
              type="checkbox"
              checked={incluirAnuladas}
              onChange={(e) => setIncluirAnuladas(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-450"
            />
            Incluir anuladas
          </label>
        </div>
      </Card>

      {/* Stats */}
      {lecturasQ.data && lecturasQ.data.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Reales" value={stats.reales} tone="ok" />
          <StatCard
            label="Estimadas"
            value={stats.estimadas}
            tone={stats.estimadas > 0 ? "warn" : "muted"}
          />
          <StatCard
            label="Con revisión"
            value={stats.con_revision}
            tone={stats.con_revision > 0 ? "warn" : "ok"}
          />
          <StatCard label="m³ totales" value={stats.total_m3} />
        </section>
      )}

      {lecturasQ.isPending && <Cargando />}
      {lecturasQ.isError && (
        <ErrorCarga
          error={lecturasQ.error}
          onReintentar={() => void lecturasQ.refetch()}
        />
      )}

      {/* Empty */}
      {lecturasQ.data && lecturasFiltradas.length === 0 && !lecturasQ.isPending && (
        (lecturasQ.data ?? []).length === 0 ? (
          <EmptyState
            icon={Gauge}
            title={`Sin lecturas en ${MESES[mes - 1]} ${anio}`}
            description="Las lecturas se toman en terreno desde la app de terreno o se cargan a mano desde aquí. Un periodo sin lecturas también puede indicar que el ciclo todavía no arranca."
          />
        ) : (
          <EmptyState
            icon={Filter}
            tone="slate"
            title="Sin resultados"
            description="Ajusta los filtros (estado, sector, búsqueda) para ver más lecturas."
          />
        )
      )}

      {/* Tabla */}
      {lecturasFiltradas.length > 0 && (
        <TableWrap label="Lecturas del periodo">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Arranque</th>
                <th scope="col" className="px-3 py-2 text-left">Fecha</th>
                <th scope="col" className="px-3 py-2 text-right">Anterior</th>
                <th scope="col" className="px-3 py-2 text-right">Actual</th>
                <th scope="col" className="px-3 py-2 text-right">Consumo</th>
                <th scope="col" className="px-3 py-2 text-right">Total</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-left">Boleta</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {lecturasFiltradas.map((l) => (
                <LecturaRow
                  key={l.id}
                  lectura={l}
                  contrato={contratoById.get(l.contrato_id)}
                />
              ))}
            </tbody>
            <tfoot className="bg-neutral-50 text-sm">
              <tr>
                <td colSpan={5} className="px-3 py-2 text-right text-neutral-500">
                  Subtotales (filtro):
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  {cantidad(lecturasFiltradas.reduce((a, l) => a + l.consumo_m3, 0))} m³
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  {formatCLP(
                    lecturasFiltradas.reduce((a, l) => a + l.monto_total, 0),
                  )}
                </td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

interface RowProps {
  lectura: LecturaRead;
  contrato: ContratoOperativo | undefined;
}

function LecturaRow({ lectura, contrato }: RowProps): JSX.Element {
  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2">
        <div className="font-medium text-neutral-900">
          {contrato?.socio_nombre ?? `contrato #${lectura.contrato_id}`}
        </div>
        <div className="text-xs text-neutral-500">{contrato?.socio_rut ?? ""}</div>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <div>{contrato?.arranque_codigo ?? "—"}</div>
        <div className="text-xs text-neutral-500">
          {contrato?.arranque_direccion ?? ""}
        </div>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        {lectura.fecha_lectura ? formatDate(lectura.fecha_lectura) : "—"}
      </td>
      <td className="px-3 py-2 text-right text-neutral-500">
        {lectura.lectura_anterior}
      </td>
      <td className="px-3 py-2 text-right">{lectura.lectura_actual}</td>
      <td className="px-3 py-2 text-right font-medium">
        {lectura.consumo_m3} m³
      </td>
      <td className="px-3 py-2 text-right font-medium">
        {formatCLP(lectura.monto_total)}
      </td>
      <td className="px-3 py-2">
        <TipoBadge tipo={lectura.tipo_lectura} />
        {lectura.flag_revision && (
          <span
            className="ml-1 inline-flex items-center gap-0.5 text-xs text-amber-700"
            title="Revisión solicitada por el operador"
          >
            <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <EstadoBadge estado={lectura.estado} />
      </td>
      <td className="px-3 py-2 text-neutral-600 font-mono text-xs">
        {lectura.numero_boleta ? (
          <span className="inline-flex items-center gap-1.5">
            {lectura.numero_boleta}
            <BoletaPdfButton
              lecturaId={lectura.id}
              numero={lectura.numero_boleta}
            />
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <AccionesLectura lectura={lectura} />
      </td>
    </tr>
  );
}

/** Descarga el PDF de una boleta emitida (para reimprimir una sola). */
function BoletaPdfButton({
  lecturaId,
  numero,
}: {
  lecturaId: number;
  numero: string;
}): JSX.Element {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  async function descargar(): Promise<void> {
    setPending(true);
    setError(false);
    try {
      await apiDownload(
        `/api/v1/lecturas/${lecturaId}/boleta-pdf`,
        `boleta_${numero}.pdf`,
      );
    } catch {
      setError(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      icon={FileDown}
      onClick={() => void descargar()}
      disabled={pending}
      aria-label={`Descargar boleta ${numero} (PDF)`}
      title={error ? "No se pudo descargar la boleta" : "Descargar boleta (PDF)"}
      className={error ? "text-rose-700 hover:text-rose-700" : "hover:text-brand-700"}
    />
  );
}

function TipoBadge({ tipo }: { tipo: string }): JSX.Element {
  return <Badge tone={TIPO_TONE[tipo] ?? "slate"}>{tipo}</Badge>;
}

function EstadoBadge({ estado }: { estado: EstadoLectura }): JSX.Element {
  return (
    <Badge tone={ESTADO_TONE[estado]}>
      {estado === "Anulada" && <EyeOff className="h-3 w-3" strokeWidth={1.5} />}
      {estado === "Pagada" && (
        <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
      )}
      {estado}
    </Badge>
  );
}

function StatCard({
  label,
  value,
  tone = "info",
}: {
  label: string;
  value: number;
  tone?: "info" | "ok" | "warn" | "muted";
}): JSX.Element {
  const map: Record<typeof tone, string> = {
    info: "bg-white border-neutral-200 text-neutral-900",
    ok: "bg-emerald-50 border-emerald-100 text-emerald-900",
    warn: "bg-amber-50 border-amber-100 text-amber-900",
    muted: "bg-neutral-50 border-neutral-100 text-neutral-700",
  };
  return (
    <div className={`rounded-lg border p-3 ${map[tone]}`}>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-xl font-semibold">{value}</div>
    </div>
  );
}
